<template>
  <div class="mini-kasko-offers">
    <Breadcrumbs :breadcrumbs="breadcrumbs"/>

    <section class="mini-kasko-offers__summary">
      <div class="mini-kasko-offers__summary-main">
        <h1 class="mini-kasko-offers__title">Знайдено: {{ filteredCompanies.length }}</h1>
        <div class="mini-kasko-offers__toolbar">
          <button
            type="button"
            class="mini-kasko-offers__toolbar-button"
            :class="{
              'mini-kasko-offers__toolbar-button--active': showEarnings,
              'mini-kasko-offers__toolbar-button--earnings': true
            }"
            @click="showEarnings = !showEarnings"
          >
            <span class="mini-kasko-offers__toolbar-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5C6.72 5 2.42 8.11 1 12c1.42 3.89 5.72 7 11 7s9.58-3.11 11-7c-1.42-3.89-5.72-7-11-7Zm0 11.5A4.5 4.5 0 1 1 12 7.5a4.5 4.5 0 0 1 0 9Zm0-2.5A2 2 0 1 0 12 10a2 2 0 0 0 0 4Z"
                  fill="#09090B"
                />
              </svg>
            </span>
            Показати заробіток
          </button>

          <div class="mini-kasko-offers__sort">
            <span class="mini-kasko-offers__toolbar-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 4H14" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 4H3" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M21 12H12" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 12H3" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M21 20H16" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 20H3" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M14 2V6" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 10V14" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M16 18V22" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="mini-kasko-offers__sort-label">Сортувати:</span>
            <div class="mini-kasko-offers__sort-options" role="group" aria-label="Сортування пропозицій">
              <button
                v-for="option in sortOptions"
                :key="option.value"
                type="button"
                class="mini-kasko-offers__sort-option"
                :class="{ 'mini-kasko-offers__sort-option--active': sortType === option.value }"
                @click="sortType = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="mini-kasko-offers__filters">
      <div class="mini-kasko-offers__filter-select" ref="filterSelect">
        <button
          type="button"
          class="mini-kasko-offers__filter-trigger"
          :class="{ 'mini-kasko-offers__filter-trigger--active': selectedCompanies.length > 0 }"
          @click="filterOpen = !filterOpen"
        >
          <span>{{ filterLabel }}</span>
          <svg class="mini-kasko-offers__filter-chevron" :class="{ 'mini-kasko-offers__filter-chevron--open': filterOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div v-if="filterOpen" class="mini-kasko-offers__filter-dropdown">
          <label class="mini-kasko-offers__filter-option">
            <input type="checkbox" :checked="selectedCompanies.length === 0" @change="clearCompanyFilter" />
            <span>Всі компанії</span>
          </label>
          <label
            v-for="name in companyNames"
            :key="name"
            class="mini-kasko-offers__filter-option"
          >
            <input type="checkbox" :value="name" v-model="selectedCompanies" />
            <span>{{ name }}</span>
          </label>
        </div>
      </div>
    </div>

    <section v-if="loading" class="mini-kasko-offers__state">
      Завантаження пропозицій...
    </section>

    <section v-else-if="errorMessage" class="mini-kasko-offers__state mini-kasko-offers__state--error">
      <p class="mini-kasko-offers__state-text">{{ errorMessage }}</p>
      <button type="button" class="mini-kasko-offers__state-action" @click="fetchOffers">
        Спробувати ще раз
      </button>
    </section>

    <section v-else-if="!filteredCompanies.length" class="mini-kasko-offers__state">
      Пропозицій не знайдено.
    </section>

    <section v-else class="mini-kasko-offers__list">
      <OfferCard
        v-for="company in filteredCompanies"
        :key="company.id"
        :offer="company"
        :show-earnings="showEarnings"
        @select-offer="openOrderForm"
      />
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import {Breadcrumbs} from '@/shared/ui/breadcrumbs';
import {HttpClient} from '@/shared/lib/http-client';
import OfferCard from './components/OfferCard.vue';

export default {
  components: {Breadcrumbs, OfferCard},
  data() {
    return {
      showEarnings: false,
      selectedCompanies: [],
      filterOpen: false,
      sortType: 'price-asc',
      sortOptions: [
        { value: 'price-asc', label: 'Дешевші' },
        { value: 'price-desc', label: 'Дорожчі' },
        { value: 'rating-desc', label: 'Рейтинг' },
      ],
      breadcrumbs: [
        { title: 'Панель управління', path: '/prime' },
        { title: 'Міні КАСКО', path: '#' },
        { title: 'Пропозиції від СК', path: '#' },
      ],
      companies: [],
      loading: false,
      errorMessage: '',
      resolvedCityId: null,
      resolvedCityName: '',
    };
  },
  computed: {
    companyNames() {
      const seen = new Set();
      return this.companies.reduce((acc, c) => {
        if (!seen.has(c.companyName)) { seen.add(c.companyName); acc.push(c.companyName); }
        return acc;
      }, []);
    },
    filterLabel() {
      if (this.selectedCompanies.length === 0) return 'Компанії';
      if (this.selectedCompanies.length === 1) return this.selectedCompanies[0];
      return `Компанії (${this.selectedCompanies.length})`;
    },
    filteredCompanies() {
      let result = this.companies.slice();

      if (this.selectedCompanies.length > 0) {
        result = result.filter((c) => this.selectedCompanies.includes(c.companyName));
      }

      if (this.sortType === 'price-desc') {
        return result.sort((a, b) => b.minPrice - a.minPrice);
      }

      if (this.sortType === 'rating-desc') {
        return result.sort((a, b) => {
          if (a.rating === b.rating) return a.minPrice - b.minPrice;
          return (b.rating || 0) - (a.rating || 0);
        });
      }

      return result.sort((a, b) => a.minPrice - b.minPrice);
    },
  },
  async created() {
    await this.resolveCityId();
    this.fetchOffers();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async resolveCityId() {
      if (this.$route.query.city_id) return;

      const copyOrderId = this.$route.query.copyOrderId;
      if (!copyOrderId) return;

      try {
        const response = await HttpClient.get(`/insurance/mini-kasko/orders/${copyOrderId}`);
        const order = response.data?.data;
        const cityId = order?.insuranceObject?.registration_city_id;
        const cityName = order?.insuranceObject?.registration_city_name || '';

        if (cityId) {
          this.resolvedCityId = cityId;
          this.resolvedCityName = cityName;
        }
      } catch {
        // не блокуємо — fetchOffers покаже помилку від API
      }
    },
    async fetchOffers() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const payload = { start_date: this.resolveStartDate() };
        const cityId = this.$route.query.city_id || this.resolvedCityId;
        if (cityId) payload.city_id = Number(cityId);

        const response = await HttpClient.post('/insurance/mini-kasko/calculate', payload);

        const data = Array.isArray(response.data.data) ? response.data.data : [];
        this.companies = data.map(this.mapCompany);
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Не вдалося завантажити пропозиції.';
        this.companies = [];
      } finally {
        this.loading = false;
      }
    },
    clearCompanyFilter() {
      this.selectedCompanies = [];
    },
    handleClickOutside(event) {
      if (this.filterOpen && this.$refs.filterSelect && !this.$refs.filterSelect.contains(event.target)) {
        this.filterOpen = false;
      }
    },
    resolveStartDate() {
      const queryDate = this.$route.query.start_date || this.$route.query.startDate;
      if (queryDate && moment(queryDate, ['YYYY-MM-DD', 'DD.MM.YYYY'], true).isValid()) {
        return moment(queryDate, ['YYYY-MM-DD', 'DD.MM.YYYY']).format('YYYY-MM-DD');
      }
      return moment().add(1, 'day').format('YYYY-MM-DD');
    },
    mapCompany(item) {
      const company = item.company || {};
      const publicName = company.publicName || company.companyName || 'Страхова компанія';
      const offers = Array.isArray(item.offers) ? item.offers : [];

      return {
        id: (company.id ?? '') + '::' + (item.programName ?? ''),
        companyName: publicName,
        programName: item.programName || null,
        title: company.title || null,
        logoName: this.resolveLogoName(publicName),
        logoSubtitle: '',
        logoAccent: this.resolveLogoAccent(publicName),
        // logoUrl: this.resolveLogoUrl(company.logo),
        logoUrl: company.logo,
        rating: null,
        company: {
          docProduct: company.docProduct || '',
          docCompany: company.docCompany || '',
          docAgent: company.docAgent || '',
          docZusp: company.docZusp || '',
          description: company.description || '',
        },
        companyLink: company.companyLink || company.website || '',
        offers: offers.map((offer) => ({
          offerId: offer.offerId,
          price: Number(offer.price) || 0,
          coverage: offer.coverage,
          earnings: offer.earnings != null ? Number(offer.earnings) : null,
          earningsPercent: offer.earningsPercent != null ? Number(offer.earningsPercent) : null,
        })),
        minPrice: offers.length ? Math.min(...offers.map((o) => Number(o.price) || 0)) : 0,
      };
    },
    openOrderForm(selectedOffer) {
      const copyOrderId = this.$route.query.copyOrderId || '';
      const route = { name: 'miniKaskoOrder' };

      if (copyOrderId) {
        route.params = { orderId: copyOrderId };
      }

      sessionStorage.setItem('miniKaskoSelectedOffer', JSON.stringify({
        offerId: selectedOffer.offerId,
        price: selectedOffer.price,
        coverage: selectedOffer.coverage,
        companyName: selectedOffer.companyName,
        earnings: selectedOffer.earnings,
        earningsPercent: selectedOffer.earningsPercent,
        startDate: this.resolveStartDate(),
        plate: this.$route.query.plate || '',
        registration_city_id: this.$route.query.city_id ? Number(this.$route.query.city_id) : (this.resolvedCityId ? Number(this.resolvedCityId) : null),
        registration_city_name: this.$route.query.city_name || this.resolvedCityName || '',
      }));

      this.$router.push(route);
    },
    resolveLogoName(companyName) {
      return companyName
        .replace(/["«»]/g, '')
        .replace(/\b(АТ|ПРАТ|ТДВ|СК)\b/gi, '')
        .trim() || 'СК';
    },
    resolveLogoUrl(logoPath) {
      if (!logoPath) return '';
      if (/^https?:\/\//i.test(logoPath)) return logoPath;
      const adminDomain = process.env.VUE_APP_ADMIN_DOMAIN || '';
      return adminDomain
        ? `${adminDomain.replace(/\/$/, '')}/${logoPath.replace(/^\//, '')}`
        : logoPath;
    },
    resolveLogoAccent(companyName) {
      const name = companyName.toLowerCase();
      if (name.includes('ingo')) return '#2e8b57';
      if (name.includes('arx')) return '#0a77ff';
      if (name.includes('uniqa')) return '#1f5db6';
      return '#fb923c';
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-offers {
  font-family: 'Geist', sans-serif;
  color: #09090b;

  &__summary {
    margin-bottom: 8px;
    padding: 8px 0 12px;
  }

  &__summary-main {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: center;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    min-height: 80px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__title {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    font-weight: 500;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__toolbar-button,
  &__sort {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 48px;
    padding: 0 16px;
    border-radius: 8px;
    border: 0;
    background: #fff;
    color: #18181b;
    font-size: 14px;
    font-weight: 500;
    box-sizing: border-box;
  }

  &__toolbar-button {
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
    justify-content: flex-start;

    &:hover { border-color: #cbd5e1; }
    &--active { background: #fff7ed; }
  }

  &__sort {
    min-width: 356px;
    padding: 6px 8px 6px 14px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
  }

  &__sort-label {
    color: #64748b;
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    white-space: nowrap;
  }

  &__sort-options {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
  }

  &__sort-option {
    min-height: 30px;
    padding: 5px 10px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #475569;
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

    &:hover { color: #0f172a; background: #f8fafc; }
    &--active {
      background: #fb923c;
      color: #ffffff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
      &:hover { color: #ffffff; background: #fb923c; }
    }
  }

  &__toolbar-icon {
    display: inline-flex;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    color: #18181b;
    svg { width: 100%; height: 100%; display: block; }
  }

  &__filters { margin-bottom: 12px; }

  &__filter-select {
    position: relative;
    display: inline-block;
  }

  &__filter-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #09090b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:hover { border-color: #94a3b8; }
    &--active { border-color: #fb923c; color: #ea580c; }
  }

  &__filter-chevron {
    transition: transform 0.2s ease;
    flex-shrink: 0;
    &--open { transform: rotate(180deg); }
  }

  &__filter-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
    padding: 4px 0;
    overflow: hidden;
  }

  &__filter-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    cursor: pointer;
    font-size: 14px;
    color: #18181b;
    transition: background-color 0.15s ease;

    &:hover { background: #f8fafc; }

    input[type="checkbox"] {
      appearance: revert;
      -webkit-appearance: revert;
      width: 16px;
      height: 16px;
      accent-color: #fb923c;
      cursor: pointer;
      flex-shrink: 0;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 24px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
    color: #52525b;
    text-align: center;
    &--error { color: #b42318; border-color: #fecaca; background: #fff5f5; }
  }

  &__state-text { margin: 0; }

  &__state-action {
    min-height: 40px;
    padding: 8px 16px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #ffffff;
    color: #09090b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }

  @media (max-width: 1050px) {
    &__summary-main { flex-direction: column; align-items: stretch; }
    &__toolbar { justify-content: flex-start; gap: 8px; }
  }

  @media (max-width: 640px) {
    &__summary { padding-top: 0; }
    &__title { font-size: 24px; line-height: 1.2; }
    &__toolbar { flex-direction: column; align-items: stretch; }
    &__toolbar-button, &__sort { width: 100%; flex: 1 1 auto; justify-content: center; }
    &__sort { height: auto; min-width: 0; justify-content: flex-start; flex-wrap: wrap; }
    &__sort-options { flex: 1 1 100%; }
    &__sort-option { flex: 1 1 0; padding-right: 8px; padding-left: 8px; }
  }
}
</style>
